import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const CalendlySection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  // Lazy load Calendly when section comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // Start loading 200px before section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Load Calendly script only when needed
  useEffect(() => {
    if (!shouldLoad) return;

    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [shouldLoad]);

  return (
    <section 
      ref={sectionRef}
      id="calendly" 
      className="py-12 md:py-16 relative overflow-hidden bg-foreground text-background"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-900 to-black" />

      <div className="container px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="font-display text-xl md:text-2xl font-bold text-white">
            Aplicar a <span className="text-gradient-gold">llamada de claridad</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="max-w-5xl mx-auto"
        >
          {shouldLoad ? (
            <div
              className="calendly-inline-widget rounded-xl overflow-hidden border border-neutral-700"
              data-url="https://calendly.com/asesor-coressenglobal/llamada?hide_gdpr_banner=1"
              style={{ minWidth: "320px", height: "650px" }}
            />
          ) : (
            <div 
              className="rounded-xl overflow-hidden border border-neutral-700 bg-neutral-800/50 flex items-center justify-center"
              style={{ minWidth: "320px", height: "650px" }}
            >
              <div className="text-center text-neutral-400">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p>Cargando calendario...</p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default CalendlySection;
